import { FormEvent, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import styled from "styled-components"

import { useEditContractorUserMutation } from "store/api/contractorsApi"
import { UITypes } from "types"
import { ContractorUserRole } from "types/ContractorUserEnum"
import styles from "assets/scss/index.module.scss"
import { ReactComponent as IconEditUser } from "assets/svg/modal-icon-add-user.svg"

import getErrorMessage from "utils/getErrorMessage"
import { editContractorUserSchema, onFormValidate, validationInitialState } from "utils/onValidate"
import { DisableContractorUserModal } from "modals/DisableContractorUserModal"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import { ToggleButton } from "components/form/ToggleButton"

import ContractorRoleSelect from "./ContractorRoleSelect"
import ContractorUserFormFields from "./ContractorUserFormFields"

const StyledModalContent = styled.div`
  padding-bottom: 0px;
`

const ToggleButtonContainer = styled.div`
  margin: 10px -20px 0;
  padding: 12px 20px 10px;
  border-top: 1px solid var(--border-default);
`

const inputValuesInitialState = {
  fullName: "",
  email: "",
  phone: "",
  isDisable: false,
  userRole: ContractorUserRole.ADMIN,
}

interface Props {
  onCancel: () => void
  onSuccess: () => void
  user?: UITypes.ContractorUser & { id: number; isDisable: boolean; userRole: number }
}

const EditContractorUserModal = ({ onCancel, onSuccess, user }: Props) => {
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [editContractorUser, { isLoading }] = useEditContractorUserMutation()
  const [inputValues, setInputValues] = useState({
    fullName: user?.fullName || "",
    email: user?.email || "",
    phone: user?.phone || "",
    isDisable: user?.isDisable || false,
    userRole: (user?.userRole as ContractorUserRole) || ContractorUserRole.ADMIN,
  })
  const [error, setError] = useState(validationInitialState)

  const onInputChange = (event: FormEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = event.currentTarget

    setInputValues(prevState => {
      return { ...prevState, [name]: type === "checkbox" ? checked : value }
    })
  }

  const onPhoneChange = (value: string) => {
    setInputValues(prevState => {
      return { ...prevState, phone: value }
    })
  }

  const onRoleChange = (value: ContractorUserRole) => {
    setInputValues(prevState => {
      return { ...prevState, userRole: value }
    })
  }

  const prepareData = () => {
    const data = {
      full_name: inputValues.fullName,
      email: inputValues.email,
      phone: inputValues.phone || "",
      is_enabled: !inputValues.isDisable,
      user_type: inputValues.userRole,
    }

    return data
  }

  const sendRequest = async () => {
    if (!user?.id) {
      toast.error("User ID is required")
      return
    }

    try {
      const res = await editContractorUser({
        id: user.id,
        body: prepareData(),
      })
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        toast.success("User has been successfully updated!")
        setInputValues(inputValuesInitialState)
        onSuccess()
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error as Error)
      toast.error(errorMessage)
    }
  }

  const onSubmit = () => {
    if (onFormValidate(inputValues, editContractorUserSchema, setError)) {
      sendRequest()
    }
  }

  const handleTrySubmit = (event: FormEvent<HTMLElement>) => {
    event.preventDefault()

    if (inputValues.isDisable && !user?.isDisable) {
      setShowDisableModal(true)
    } else {
      onSubmit()
    }
  }

  const handleSubmit = () => {
    setShowDisableModal(false)
    onSubmit()
  }

  const onCancelClick = () => {
    onCancel()
  }

  const isFormValid = inputValues.fullName.trim() !== "" && inputValues.email.trim() !== ""

  return (
    <>
      {createPortal(
        <ModalLayout
          width="450px"
          confirmButtonText="Save"
          confirmButtonType="submit"
          onConfirm={handleTrySubmit}
          onCancel={onCancelClick}
          isFetching={isLoading}
          isDisabled={!isFormValid}
        >
          <ModalHeader title="Edit user">
            <IconEditUser />
          </ModalHeader>
          <StyledModalContent className={styles.modalContent}>
            <ContractorUserFormFields
              fullName={inputValues.fullName}
              email={inputValues.email}
              phone={inputValues.phone}
              onInputChange={onInputChange}
              onPhoneChange={onPhoneChange}
              error={error}
            />
            <div>
              <ContractorRoleSelect selectedRole={inputValues.userRole} onChange={onRoleChange} />
              {error.field === "userRole" && <div className={styles.errorMessage}>{error.message}</div>}
            </div>
            <ToggleButtonContainer>
              <ToggleButton id="isDisable" name="isDisable" isChecked={inputValues.isDisable} onChange={onInputChange}>
                Disable user
              </ToggleButton>
            </ToggleButtonContainer>
          </StyledModalContent>
        </ModalLayout>,
        document.body,
      )}
      {showDisableModal && (
        <DisableContractorUserModal onConfirm={handleSubmit} onCancel={() => setShowDisableModal(false)} />
      )}
    </>
  )
}

export default EditContractorUserModal
